import { DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
import { localDate, localTimeOfDay, utcOffsetMinutesAt, zonedWallTimeToInstant, } from "./time-zone.js";
// The hard constraints of a plan (CHT-746, contract `docs/architecture/milestone-d-contracts.md` 6.3). The model PROPOSES a plan and
// names what the customer said they need (dates, diet, access, a fixed dinner, items to do or to avoid). The server CHECKS the plan
// against those words, with data it reads itself (the structured tags of a catalog item, the zones of the trip), and refuses a plan
// that breaks one of them with a closed list of conflicts. Pure and total: it reads no clock and no database.
//
// Precedence (contract 6.3): what the customer says NOW is a constraint; memory and the ranking of a search are not. A constraint
// that the customer did not state (`null`) is not checked, and memory never fills it in: the planner only reports that memory holds
// something the customer did not repeat, and asks. A constraint that the customer stated as "none" (`[]`) is checked as "none".
export const planPaceCodes = Object.freeze(["RELAXED", "BALANCED", "PACKED"]);
/** Activities a day may hold before the pace is exceeded (meals and transit are not activities). A soft limit. */
export const PACE_MAX_ACTIVITIES_PER_DAY = Object.freeze({
    RELAXED: 3,
    BALANCED: 5,
    PACKED: 8,
});
export const planBudgetBands = Object.freeze(["ECONOMY", "MODERATE", "PREMIUM", "LUXURY"]);
export const planMealPeriods = Object.freeze(["BREAKFAST", "LUNCH", "DINNER", "SNACK"]);
/** The most days a plan made in one call may cover, and the most of each list. Bounds, not business rules. */
export const PLAN_CONSTRAINT_BOUNDS = Object.freeze({
    maxDays: 21,
    maxDietary: 10,
    maxAccess: 10,
    maxCommitments: 10,
    maxItemRefs: 20,
    maxDestinations: 10,
    commitmentLabelMax: 120,
});
/** The key of an item in a plan the model sent: day, then item, both counted from 1. */
const PLANNER_KEY = /^d[1-9]\d{0,1}i[1-9]\d{0,2}$/;
/** A lowercase code such as `vegetarian` or `step_free`: the vocabulary of catalog tags. */
const CODE = /^[a-z][a-z0-9_]{0,39}$/;
const fail = (field, rule) => new DomainValidationError(field, rule);
function codes(field, value, max) {
    if (value === null || value === undefined)
        return null;
    if (value.length > max)
        throw fail(field, `has more than ${max} entries`);
    const seen = new Set();
    for (const entry of value) {
        if (typeof entry !== "string" || !CODE.test(entry)) {
            throw fail(field, "must hold lowercase codes such as vegetarian or step_free");
        }
        seen.add(entry);
    }
    return Object.freeze([...seen].sort());
}
function ids(field, value) {
    if (value === null || value === undefined)
        return Object.freeze([]);
    if (value.length > PLAN_CONSTRAINT_BOUNDS.maxItemRefs) {
        throw fail(field, `has more than ${PLAN_CONSTRAINT_BOUNDS.maxItemRefs} entries`);
    }
    return Object.freeze([...new Set(value)]);
}
const DAY_MS = 86_400_000;
/** The calendar days of a range, both ends included. Dates are plain dates: no zone is involved. */
export function datesInRange(dateFrom, dateTo) {
    const from = Date.parse(`${dateFrom}T00:00:00Z`);
    const to = Date.parse(`${dateTo}T00:00:00Z`);
    const days = [];
    for (let at = from; at <= to; at += DAY_MS)
        days.push(new Date(at).toISOString().slice(0, 10));
    return days;
}
/** Checks the shape of what the model passed and freezes it. A bad value is a validation error that names the field. */
export function normalizePlanConstraints(input) {
    const dateFrom = localDate(input.dateFrom, "constraints.dateFrom");
    const dateTo = localDate(input.dateTo, "constraints.dateTo");
    if (dateTo < dateFrom)
        throw fail("constraints.dateTo", "must not be before the first date");
    const days = (Date.parse(`${dateTo}T00:00:00Z`) - Date.parse(`${dateFrom}T00:00:00Z`)) / DAY_MS;
    if (days + 1 > PLAN_CONSTRAINT_BOUNDS.maxDays) {
        throw fail("constraints.dateTo", `must be within ${PLAN_CONSTRAINT_BOUNDS.maxDays} days`);
    }
    const commitments = (input.fixedCommitments ?? []).map((commitment, index) => {
        const field = `constraints.fixedCommitments[${index}]`;
        const label = commitment.label.trim();
        if (label.length < 1 || label.length > PLAN_CONSTRAINT_BOUNDS.commitmentLabelMax) {
            throw fail(`${field}.label`, "must be 1 to 120 characters");
        }
        const date = localDate(commitment.date, `${field}.date`);
        if (date < dateFrom || date > dateTo)
            throw fail(`${field}.date`, "must be inside the dates");
        const duration = commitment.durationMinutes;
        if (duration !== null && (!Number.isInteger(duration) || duration < 1 || duration > 1_440)) {
            throw fail(`${field}.durationMinutes`, "must be 1 to 1440 minutes");
        }
        const itemKey = commitment.itemKey ?? null;
        if (itemKey !== null && !PLANNER_KEY.test(itemKey)) {
            throw fail(`${field}.itemKey`, "must be a planner key such as d1i4");
        }
        return Object.freeze({
            label,
            date,
            time: localTimeOfDay(commitment.time, `${field}.time`).slice(0, 5),
            timeZone: commitment.timeZone,
            durationMinutes: duration,
            itemKey,
            lineageId: commitment.lineageId ?? null,
        });
    });
    if (commitments.length > PLAN_CONSTRAINT_BOUNDS.maxCommitments) {
        throw fail("constraints.fixedCommitments", "has too many commitments");
    }
    const destinations = (input.destinations ?? []).slice(0, PLAN_CONSTRAINT_BOUNDS.maxDestinations);
    return Object.freeze({
        dateFrom,
        dateTo,
        destinations: Object.freeze(destinations.map((destination) => Object.freeze({
            placeName: destination.placeName.trim(),
            countryCode: destination.countryCode ?? null,
        }))),
        party: input.party ?? null,
        pace: input.pace ?? null,
        dietary: codes("constraints.dietary", input.dietary, PLAN_CONSTRAINT_BOUNDS.maxDietary),
        accessNeeds: codes("constraints.accessNeeds", input.accessNeeds, PLAN_CONSTRAINT_BOUNDS.maxAccess),
        fixedCommitments: Object.freeze(commitments),
        budgetBand: input.budgetBand ?? null,
        mustDo: ids("constraints.mustDo", input.mustDo),
        mustAvoid: ids("constraints.mustAvoid", input.mustAvoid),
    });
}
// --- Conflicts ------------------------------------------------------------------------------------------------------------
/**
 * Why a plan was refused or flagged. Closed: the model branches on the code, and the customer words are written from it.
 * HARD conflicts refuse the plan. SOFT ones are returned with the persisted plan.
 */
export const planConflictCodes = Object.freeze([
    /** A meal is at a catalog restaurant that lacks a structured tag for a stated dietary need. */
    "DIETARY_NOT_MET",
    /** A meal is not in the catalog, so no structured tag can certify the dietary need. */
    "DIETARY_NOT_VERIFIABLE",
    /** A catalog item lacks a structured tag for a stated access need. */
    "ACCESS_NOT_MET",
    /** An item outside the catalog: no structured tag can certify the access need. (soft) */
    "ACCESS_NOT_VERIFIABLE",
    /** The plan has no item at the wall time of a fixed commitment, and no item with its words. */
    "FIXED_COMMITMENT_MISSING",
    /** The plan has the commitment, at another time. */
    "FIXED_COMMITMENT_MOVED",
    /** The wall time of a fixed commitment does not exist or happens twice in its zone. */
    "FIXED_COMMITMENT_TIME_INVALID",
    "MUST_AVOID_PRESENT",
    "MUST_DO_MISSING",
    /** A day section lies outside the dates the customer gave. */
    "DAY_OUTSIDE_DATES",
    /** A scheduled item has a zone that no catalog item and no part of the trip confirms. */
    "ZONE_UNCONFIRMED",
    /** An edit of an item that exists names another time zone (or end zone) than the one it holds: only the customer changes a zone. */
    "ZONE_CHANGE_NOT_ALLOWED",
    /** A day holds more activities than the pace allows. (soft) */
    "PACE_EXCEEDED",
    /** A date of the range has no day in the plan. (soft) */
    "DAY_NOT_PLANNED",
]);
const SOFT = new Set([
    "ACCESS_NOT_VERIFIABLE",
    "PACE_EXCEEDED",
    "DAY_NOT_PLANNED",
]);
export const planConflictSeverityOf = (code) => SOFT.has(code) ? "SOFT" : "HARD";
const conflict = (code, fields = {}) => Object.freeze({
    code,
    severity: planConflictSeverityOf(code),
    itemKey: fields.itemKey ?? null,
    catalogItemId: fields.catalogItemId ?? null,
    missing: Object.freeze([...(fields.missing ?? [])]),
    expected: fields.expected ?? null,
    found: fields.found ?? null,
});
const hasTag = (item, namespaces, code) => item.catalogTags.some((tag) => namespaces.includes(tag.namespace) && tag.code === code);
const isMeal = (item) => item.catalogType === "DINING" || item.meal !== null;
/** The wall time of an instant in a zone, as text (`2026-05-02`, `19:30`). */
export const wallTimeTextOf = (instant, zone) => {
    const offset = utcOffsetMinutesAt(instant, zone);
    const local = new Date(instant.getTime() + offset * 60_000).toISOString();
    return { date: local.slice(0, 10), time: local.slice(11, 16), timeZone: zone };
};
const wallOf = wallTimeTextOf;
/** Two zones read the same at an instant when their offsets agree there (`Asia/Calcutta` is `Asia/Kolkata`). */
function sameZoneAt(a, b, instant) {
    if (a === b)
        return true;
    try {
        return utcOffsetMinutesAt(instant, a) === utcOffsetMinutesAt(instant, b);
    }
    catch {
        return false;
    }
}
/**
 * Checks a planned content against what the customer said. The conflicts come in a fixed order (by rule, then by item), so one input
 * gives one list. `HARD` conflicts refuse the plan; the caller returns them to the model, which may retry once.
 */
export function checkPlanAgainstConstraints(input) {
    const { constraints, items, days } = input;
    const found = [];
    // 1. The days lie inside the dates the customer gave.
    for (const date of days) {
        if (date < constraints.dateFrom || date > constraints.dateTo) {
            found.push(conflict("DAY_OUTSIDE_DATES", { missing: [date] }));
        }
    }
    // 2. A dietary need is met by a structured tag, never by a name, a note or a ranking.
    const dietary = constraints.dietary ?? [];
    if (dietary.length > 0) {
        for (const item of items) {
            if (item.unchanged === true || !isMeal(item))
                continue;
            if (item.catalogItemId === null || item.catalogType !== "DINING") {
                found.push(conflict("DIETARY_NOT_VERIFIABLE", { itemKey: item.key, missing: dietary }));
                continue;
            }
            const missing = dietary.filter((code) => !hasTag(item, ["DIETARY"], code));
            if (missing.length > 0) {
                found.push(conflict("DIETARY_NOT_MET", {
                    itemKey: item.key,
                    catalogItemId: item.catalogItemId,
                    missing,
                }));
            }
        }
    }
    // 3. An access need is met by a structured feature or amenity tag.
    const access = constraints.accessNeeds ?? [];
    if (access.length > 0) {
        for (const item of items) {
            if (item.unchanged === true || item.kind === "TRANSIT" || item.kind === "BUFFER")
                continue;
            if (item.catalogItemId === null) {
                found.push(conflict("ACCESS_NOT_VERIFIABLE", { itemKey: item.key, missing: access }));
                continue;
            }
            const missing = access.filter((code) => !hasTag(item, ["FEATURE", "AMENITY"], code));
            if (missing.length > 0) {
                found.push(conflict("ACCESS_NOT_MET", {
                    itemKey: item.key,
                    catalogItemId: item.catalogItemId,
                    missing,
                }));
            }
        }
    }
    // 4. Items to avoid are absent and items to do are present.
    const avoided = new Set(constraints.mustAvoid);
    for (const item of items) {
        if (item.unchanged !== true && item.catalogItemId !== null && avoided.has(item.catalogItemId)) {
            found.push(conflict("MUST_AVOID_PRESENT", { itemKey: item.key, catalogItemId: item.catalogItemId }));
        }
    }
    const present = new Set(items.flatMap((item) => (item.catalogItemId === null ? [] : [item.catalogItemId])));
    for (const id of constraints.mustDo) {
        if (!present.has(id))
            found.push(conflict("MUST_DO_MISSING", { catalogItemId: id }));
    }
    // 5. A fixed commitment is ONE item of the plan, at its own wall time and with its own length, whatever else moves.
    for (const commitment of constraints.fixedCommitments) {
        const expected = {
            date: commitment.date,
            time: commitment.time,
            timeZone: commitment.timeZone,
        };
        let instant;
        try {
            instant = zonedWallTimeToInstant(commitment.date, commitment.time, commitment.timeZone);
        }
        catch (error) {
            if (error instanceof DomainRuleViolationError || error instanceof DomainValidationError) {
                found.push(conflict("FIXED_COMMITMENT_TIME_INVALID", { expected }));
                continue;
            }
            throw error;
        }
        // The item that IS the commitment: by its stable id when the plan is stored, else by the key the model named. No other item counts.
        const item = commitment.lineageId !== null
            ? items.find((candidate) => candidate.lineageId === commitment.lineageId)
            : commitment.itemKey === null
                ? undefined
                : items.find((candidate) => candidate.key === commitment.itemKey);
        if (item === undefined) {
            found.push(conflict("FIXED_COMMITMENT_MISSING", { expected }));
            continue;
        }
        const startOk = item.startsAt !== null && item.startsAt.getTime() === instant.instant.getTime();
        const lengthOk = commitment.durationMinutes === null ||
            item.durationMinutes === null ||
            item.durationMinutes === commitment.durationMinutes;
        if (!startOk || !lengthOk) {
            found.push(conflict("FIXED_COMMITMENT_MOVED", {
                itemKey: item.key,
                expected,
                found: item.startsAt === null || item.timeZone === null
                    ? null
                    : wallOf(item.startsAt, item.timeZone),
            }));
        }
    }
    // 6. A zone the model typed is not a confirmed zone: the catalog item's own, or one the trip carries.
    for (const item of items) {
        if (!item.isNew || item.startsAt === null || item.timeZone === null)
            continue;
        const zone = item.timeZone;
        const startsAt = item.startsAt;
        const confirmed = (item.catalogTimeZone !== null && sameZoneAt(item.catalogTimeZone, zone, startsAt)) ||
            input.tripZones.some((tripZone) => sameZoneAt(tripZone, zone, startsAt));
        if (!confirmed)
            found.push(conflict("ZONE_UNCONFIRMED", { itemKey: item.key }));
    }
    // 6b. The agent never changes the zone of an item that exists (nor its end zone): only the customer does, in the app.
    for (const item of items) {
        if (item.isNew || item.zoneEdit === undefined || item.zoneEdit === null)
            continue;
        const { stored, requested } = item.zoneEdit;
        const at = item.startsAt ?? new Date(0);
        const differs = (kept, named) => named !== null && (kept === null || !sameZoneAt(kept, named, at));
        if (differs(stored.start, requested.start) ||
            differs(stored.end ?? stored.start, requested.end)) {
            found.push(conflict("ZONE_CHANGE_NOT_ALLOWED", { itemKey: item.key }));
        }
    }
    // 7. Soft: the pace, and days of the range that the plan leaves out.
    if (constraints.pace !== null) {
        const limit = PACE_MAX_ACTIVITIES_PER_DAY[constraints.pace];
        const perDay = new Map();
        for (const item of items) {
            if (item.localDate === null || item.kind === "TRANSIT" || item.kind === "BUFFER")
                continue;
            if (isMeal(item))
                continue;
            perDay.set(item.localDate, (perDay.get(item.localDate) ?? 0) + 1);
        }
        for (const [date, count] of [...perDay].sort(([a], [b]) => (a < b ? -1 : 1))) {
            if (count > limit)
                found.push(conflict("PACE_EXCEEDED", { missing: [date] }));
        }
    }
    const planned = new Set(days);
    const unplanned = datesInRange(constraints.dateFrom, constraints.dateTo).filter((date) => !planned.has(date));
    if (unplanned.length > 0)
        found.push(conflict("DAY_NOT_PLANNED", { missing: unplanned }));
    return Object.freeze(found);
}
export const hardConflictsOf = (conflicts) => conflicts.filter((entry) => entry.severity === "HARD");
